import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, BookOpen, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';

export default function LongCovid() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    {
      title: "Long COVID: A Longevity, Functional Medicine, and Health Optimization Perspective",
      subtitle: "Unraveling Pathophysiology and Advancing Clinical Strategies",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-50 to-blue-50 p-8 rounded-lg text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Long COVID: A Longevity, Functional Medicine, and Health Optimization Perspective
            </h1>
            <h2 className="text-xl text-gray-700 mb-6">Unraveling Pathophysiology and Advancing Clinical Strategies</h2>
            
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto">
              <div className="space-y-3 text-gray-600">
                <p className="text-base"><strong>Focus:</strong> Post-Acute Sequelae of SARS-CoV-2 (PASC)</p>
                <p className="text-base"><strong>Approach:</strong> Systems Biology & Personalized Medicine</p>
                <p className="text-base"><strong>Goal:</strong> Restore Cellular Health & Optimize Healthspan</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Comprehensive Clinical Framework</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>Evidence-Based:</strong> Cutting-edge research on Long COVID pathophysiology</p>
              <p><strong>Systems Approach:</strong> Interconnected mechanisms and multi-modal interventions</p>
              <p><strong>Personalized:</strong> Biomarker-guided, individualized treatment strategies</p>
              <p><strong>Longevity Focus:</strong> Beyond symptom management to cellular optimization</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Long COVID Challenge: A New Frontier in Chronic Illness",
      subtitle: "Lecture Overview & The Long COVID Challenge",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">What is Long COVID?</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Definition:</strong> An infection-associated chronic condition (IACC) with signs, symptoms, and conditions continuing or developing after initial SARS-CoV-2 infection.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Duration:</strong> Present for at least 3 months, potentially multisystemic, relapsing-remitting, with possible severe events even years later (National Academies).</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Prevalence:</strong> Affects 10-30%+ of individuals post-infection.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Clinical Approach</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Goal:</strong> Move beyond symptom management to restore cellular health and optimize long-term wellness.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Why This Perspective:</strong> Focus on root causes, systems biology, and personalized interventions to promote resilience and healthspan.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Framework:</strong> Longevity medicine principles applied to chronic post-viral illness recovery and optimization.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Heterogeneous Nature of Long COVID",
      subtitle: "Understanding Long COVID: A Complex, Multisystemic Condition",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Presentation</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Broad, Persistent, Debilitating Symptoms:</strong> Fatigue, cognitive dysfunction ("brain fog"), cardiovascular, GI, neuropsychiatric issues.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Heterogeneity:</strong> Symptoms vary widely between individuals and can fluctuate (relapse/remit).</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p>This suggests multiple, interconnected underlying pathophysiological mechanisms.</p>
                <p className="mt-2">Not a single, linear cause; requires a systems-biology approach.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Longevity & Functional Medicine Framework</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Core Principles:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Identify and address root causes of dysfunction</li>
                  <li>Consider interconnectedness of bodily systems</li>
                  <li>Leverage strategies promoting longevity and optimal healthspan</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Key Insight:</strong> Mechanisms (chronic inflammation, mitochondrial dysfunction, senescence) are hallmarks of aging and chronic disease.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Pathophysiological Drivers of Long COVID",
      subtitle: "Core Pathophysiological Mechanisms: An Interconnected Web",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Interconnected Mechanisms</h3>
            <p className="text-sm text-gray-800 mb-6">Long COVID is driven by a complex interplay of processes. Understanding these is key to targeted therapy.</p>
            
            <svg width="600" height="400" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#555" />
                </marker>
              </defs>
              <circle cx="300" cy="200" r="50" fill="#FFDDC1" stroke="#FFA07A" strokeWidth="2"/>
              <text x="300" y="195" textAnchor="middle" dy=".3em" fontSize="12" fontWeight="bold">SARS-CoV-2</text>
              <text x="300" y="210" textAnchor="middle" dy=".3em" fontSize="12" fontWeight="bold">Infection</text>

              <circle cx="100" cy="100" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="100" y="95" textAnchor="middle" fontSize="10">Immune Dysreg.</text>
              <text x="100" y="108" textAnchor="middle" fontSize="10">& Chronic Inflam.</text>
              <line x1="260" y1="170" x2="140" y2="110" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <circle cx="500" cy="100" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="500" y="95" textAnchor="middle" fontSize="10">Microclots &</text>
              <text x="500" y="108" textAnchor="middle" fontSize="10">Endothelial Dys.</text>
              <line x1="340" y1="170" x2="460" y2="110" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <circle cx="100" cy="300" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="100" y="295" textAnchor="middle" fontSize="10">Mito/Metabolic</text>
              <text x="100" y="308" textAnchor="middle" fontSize="10">Dysfunction</text>
              <line x1="260" y1="230" x2="140" y2="290" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <circle cx="500" cy="300" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="500" y="295" textAnchor="middle" fontSize="10">Redox Imbalance</text>
              <text x="500" y="308" textAnchor="middle" fontSize="10">& Nrf2 Inhibition</text>
              <line x1="340" y1="230" x2="460" y2="290" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <circle cx="300" cy="50" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="300" y="45" textAnchor="middle" fontSize="10">Cell Senescence</text>
              <text x="300" y="58" textAnchor="middle" fontSize="10">& Autophagy Dis.</text>
              <line x1="300" y1="150" x2="300" y2="90" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <circle cx="300" cy="350" r="40" fill="#E6E6FA" stroke="#9370DB" strokeWidth="1.5"/>
              <text x="300" y="345" textAnchor="middle" fontSize="10">Neuroinflam. &</text>
              <text x="300" y="358" textAnchor="middle" fontSize="10">Gut-Brain Axis</text>
              <line x1="300" y1="250" x2="300" y2="310" stroke="#777" strokeWidth="1.5" markerEnd="url(#arrow)"/>

              <path d="M 135 125 Q 200 150 265 125" stroke="#B0C4DE" strokeWidth="1" fill="none" strokeDasharray="2,2"/>
              <path d="M 465 125 Q 400 150 335 125" stroke="#B0C4DE" strokeWidth="1" fill="none" strokeDasharray="2,2"/>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Immune System in Overdrive: Chronic Inflammation",
      subtitle: "Mechanism 1: Persistent Immune Dysregulation & Chronic Inflammation",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Sustained inflammasome activation (monocytes):</strong> ↑ IL-1β</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Persistent pro-inflammatory cytokines:</strong> IL-1β, IL-6, TNF-α, IFN-γ → neuroinflammation, fatigue</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>T-cell dysregulation:</strong> Exhaustion (↑PD-1), senescent CD8+ T-cells (↑p16INK4a)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Monocyte abnormalities:</strong> Immature, pro-inflammatory monocytes</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Neutrophil Extracellular Traps (NETs):</strong> Linked to ongoing inflammation, fibrosis, cardiovascular issues</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Biomarkers:</strong> hsCRP, ESR, Ferritin, Cytokine panel (IL-6, TNF-α, IL-1β), T-cell subset analysis (CD4/CD8, PD-1, CD57, p16INK4a), Soluble CD14/CD163</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Therapeutic Considerations:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Targeted anti-inflammatories (Curcumin, Boswellia, Omega-3s)</li>
                  <li>Immunomodulators (Low-Dose Naltrexone, Thymosin Alpha-1 - investigational)</li>
                  <li>Consider senolytics for senescent immune cells</li>
                  <li>Address sources of inflammation (gut, viral persistence)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "'Sticky Blood': Microclots & Damaged Vessels",
      subtitle: "Mechanism 2: Coagulopathy, Microclots & Endothelial Dysfunction",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Fibrinaloid Microclots:</strong> Anomalous, amyloid-like fibrin clots resistant to fibrinolysis</p>
                <p className="mt-2">• Trap pro-inflammatory molecules (VWF, PF4), anti-fibrinolytic factors (α2-antiplasmin)</p>
                <p className="mt-2">• Spike protein may "seed" formation</p>
                <p className="mt-2">• Impair microcirculation → tissue hypoxia → fatigue, brain fog, dyspnea</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Platelet Hyperactivation:</strong> Contributes to microclots and endothelial inflammation</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Endotheliitis:</strong> Widespread endothelial damage → impaired vasodilation (↓NO), increased permeability</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Biomarkers:</strong></p>
                <p className="mt-2"><strong>Standard:</strong> D-dimer (often normal), Fibrinogen, PAI-1, vWF antigen/activity, Platelet Factor 4</p>
                <p className="mt-2"><strong>Specialized:</strong> Microclot detection assays (fluorescence microscopy - research/specialty labs)</p>
                <p className="mt-2"><strong>Endothelial:</strong> sVCAM-1, sICAM-1, Flow-Mediated Dilation (FMD), IL-6, NLR</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Therapeutic Considerations:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Fibrinolytic enzymes: Nattokinase (2000-4000 FU BID), Lumbrokinase (20-40mg TID)</li>
                  <li>Anti-platelet agents: Aspirin (low-dose), Clopidogrel (if indicated)</li>
                  <li>Endothelial support: L-arginine/L-citrulline, Pycnogenol, Vitamin C, Omega-3s</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Energy Crisis: Failing Mitochondria",
      subtitle: "Mechanism 3: Metabolic Derangement & Mitochondrial Dysfunction",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Impaired mitochondrial respiration & ATP production</strong></p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Structural mitochondrial damage</strong> (swelling, altered morphology)</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Increased mitochondrial ROS production</strong> → oxidative stress</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Altered glycolysis,</strong> potential hypometabolic state</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Thiamine (B1) Deficiency Link:</strong> Crucial for glucose metabolism & mitochondrial energy (pyruvate dehydrogenase cofactor). Deficiency can mimic/worsen fatigue, dysautonomia, cognitive issues.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Biomarkers:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Organic Acids Test (OATs): Krebs cycle intermediates, fatty acid oxidation markers</li>
                  <li>Blood: Lactate, Pyruvate, CoQ10 levels, Thiamine (whole blood thiamine diphosphate)</li>
                  <li>Mitochondrial function assays (specialized)</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Mitochondrial Support "Cocktail":</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Coenzyme Q10 (Ubiquinol): 100-300mg+ daily</li>
                  <li>N-Acetylcysteine (NAC): 600-1200mg BID</li>
                  <li>Acetyl-L-Carnitine: 500-1000mg BID-TID</li>
                  <li>Alpha-Lipoic Acid (R-ALA): 300-600mg BID</li>
                  <li>High-dose Thiamine/Benfotiamine: 50-100mg+ TID or 150-300mg BID</li>
                  <li>Magnesium, D-Ribose, PQQ</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Oxidative Stress Overload & Weakened Defenses",
      subtitle: "Mechanism 4: Redox Imbalance & Nrf2 Inhibition",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Increased ROS Production:</strong> From dysfunctional mitochondria, activated immune cells</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Depleted Antioxidant Capacity:</strong> Notably Glutathione (GSH) depletion</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>NADPH "Steal":</strong> Diversion of NADPH to combat ROS, impairing other pathways</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Nrf2 Pathway Inhibition:</strong> SARS-CoV-2 inhibits Nrf2, the master regulator of antioxidant response. This cripples endogenous antioxidant enzyme production (SOD, Catalase, GPx).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Vicious Cycle:</strong> Oxidative stress fuels inflammation (NF-κB activation), and inflammation generates more ROS</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Biomarkers:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Glutathione (total & reduced), GSH/GSSG ratio</li>
                  <li>Lipid peroxides (MDA, F2-isoprostanes)</li>
                  <li>Protein carbonyls, DNA damage (8-OHdG)</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Therapeutic Considerations:</strong></p>
                <p className="mt-2"><strong>Glutathione support:</strong> Liposomal GSH (250-500mg daily), NAC (600-1200mg BID), Glycine</p>
                <p className="mt-2"><strong>Other Antioxidants:</strong> Vitamin C (IV or oral), Vitamin E, Selenium, ALA</p>
                <p className="mt-2"><strong>Nrf2 Activators:</strong> Sulforaphane, Curcumin, Resveratrol, EGCG, diet rich in colorful plants</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "'Zombie Cells' & Impaired Cellular Cleanup",
      subtitle: "Mechanism 5: Cellular Senescence & Disrupted Autophagy",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Virus-Induced Senescence (VIS):</strong> SARS-CoV-2 (esp. spike protein) can directly induce premature cellular senescence (lung, endothelial, immune cells)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>SASP (Senescence-Associated Secretory Phenotype):</strong> Senescent cells secrete pro-inflammatory cytokines (IL-6, IL-1β, TNF-α), chemokines (CXCL8, CXCL10), GDF15, MMPs, fueling chronic inflammation & paracrine senescence</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Autophagy Inhibition:</strong> SARS-CoV-2 proteins (NSP6, ORF3a, ORF7a) block autophagy, impairing clearance of damaged organelles (mitophagy) and viral debris</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Persistence:</strong> Senescent T-cells (PD-1+, CD57+, p16INK4a+) and monocytes persist in Long COVID</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Biomarkers (Research/Specialty):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Senescence markers: p16INK4a, p21, SA-β-gal, circulating SASP factors</li>
                  <li>Autophagy markers: LC3-II/I ratio, p62/SQSTM1</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Senolytics (Clear senescent cells):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Dasatinib (100mg) + Quercetin (1000-1250mg) - pulsed dosing (2-3 days every 2-4 weeks)</li>
                  <li>Fisetin (20mg/kg or 1000-1500mg daily for 2-3 days, pulsed)</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Autophagy Enhancers:</strong> Spermidine (1-6mg daily), Resveratrol, Intermittent Fasting/TRE, Rapamycin</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Brain Fog & Beyond: Neuroinflammation and the Gut Connection",
      subtitle: "Mechanism 6: Neuroinflammation & Gut-Brain-Immune Axis",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Neuroinflammation Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Microglial/Astrocyte Activation:</strong> Peripheral inflammation (cytokines) crosses compromised BBB or signals via vagus nerve</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Neurotransmitter Imbalance:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Dopamine depletion (MAO-B overactivity, neuronal damage)</li>
                  <li>Serotonin depletion (IDO1 shunts tryptophan to kynurenine, gut dysbiosis)</li>
                  <li>Kynurenine pathway activation → neurotoxic metabolites (quinolinic acid)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Gut-Brain-Immune Axis Features</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Gut dysbiosis:</strong> ↓ beneficial bacteria (e.g., Faecalibacterium prausnitzii → ↓ butyrate)</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Increased intestinal permeability ("Leaky Gut"):</strong> Allows LPS, antigens into circulation → systemic & neuroinflammation</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Biomarkers:</strong></p>
                <p className="mt-2"><strong>Neuro:</strong> Neurotransmitter metabolites (urine), Kynurenine/Tryptophan ratio</p>
                <p className="mt-2"><strong>Gut:</strong> Comprehensive stool analysis, Zonulin, LPS antibodies</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Therapeutic Considerations:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Neuro-inflammation: Curcumin, Resveratrol, EGCG, PEA</li>
                  <li>Neurotransmitter support: L-Tyrosine, 5-HTP/L-Tryptophan (cautiously), B-vitamins</li>
                  <li>Gut restoration: Probiotics, Prebiotics, Butyrate, L-Glutamine, Zinc Carnosine</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Interconnected Pathways: Why a Systems Approach is Vital",
      subtitle: "The Web of Dysfunction: Cross-Talk & Core Signaling Pathways",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Detrimental Feedback Loops</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Neuro-Immune-Mitochondrial:</strong> Inflammation damages mitochondria; mitochondrial dysfunction impairs immune cells; neuroinflammation affects systemic immunity</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Gut-Immune-Brain:</strong> Gut dysbiosis → systemic inflammation → neuroinflammation</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Senescence-mTOR-Autophagy:</strong> mTOR overactivation inhibits autophagy, promotes senescence; SASP fuels mTOR</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Hormonal-Immune:</strong> Endocrine imbalances (thyroid, adrenal, sex hormones) impact immune resilience</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Targeting Core Signaling Pathways</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Convergence Point:</strong> Many Long COVID mechanisms converge on dysregulated cellular signaling pathways</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Key Pathways:</strong> Insulin/IGF-1, mTOR, AMPK, NAD⁺/Sirtuins, NF-κB, JAK/STAT, TGF-β, Nrf2, RAAS</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Strategic Advantage:</strong> These pathways govern health, aging, and chronic disease. Identifying and modulating these offers precise therapeutic levers</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hijacked Cell Signals (1): Metabolism & Growth Control",
      subtitle: "Signaling: Insulin/IGF-1, AMPK & mTOR Dysregulation",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Insulin/IGF-1 Signaling (IIS)</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Dysregulation:</strong> SARS-CoV-2 impairs IIS (via IRF1 upregulation), promoting insulin resistance. Persists in PASC, risk of metabolic sequelae</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Clinical Levers:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Metformin: 500mg OD to 1000mg BID</li>
                  <li>Berberine: 500mg BID-TID</li>
                  <li>Alpha-Lipoic Acid: 300-600mg BID</li>
                  <li>Low-glycemic diet, paced exercise</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">AMPK & mTOR Pathways</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>AMPK (Energy Sensor):</strong></p>
                <p className="mt-2">Dysregulation: SARS-CoV-2 inhibits AMPK, impairing energy production and autophagy</p>
                <p className="mt-2">Clinical Levers: Metformin, Berberine, Resveratrol (200-500mg+), Exercise (paced)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>mTOR (Growth Regulator):</strong></p>
                <p className="mt-2">Dysregulation: mTORC1 hyperactivation ("TorS" hypothesis) → inhibits autophagy, promotes inflammation, T-cell exhaustion</p>
                <p className="mt-2">Clinical Levers: Rapamycin (2-6mg/week), Metformin, Resveratrol, Spermidine (6mg/day)</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hijacked Cell Signals (2): Longevity, Inflammation & Immunity",
      subtitle: "Signaling: NAD⁺/Sirtuins, NF-κB & JAK/STAT Dysregulation",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">NAD⁺/Sirtuins & NF-κB</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>NAD⁺/Sirtuins (Longevity & Repair):</strong></p>
                <p className="mt-2">Dysregulation: NAD⁺ depletion in COVID-19 (↑CD38, PARPs), persists in Long COVID, impairing Sirtuin activity</p>
                <p className="mt-2">Clinical Levers: Nicotinamide Riboside (250-1000mg/day), NMN (250-500mg+/day), Resveratrol</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>NF-κB (Inflammation Master Switch):</strong></p>
                <p className="mt-2">Dysregulation: Persistent NF-κB activation by viral components/ROS → chronic cytokine production</p>
                <p className="mt-2">Clinical Levers: Curcumin (500-1000mg BID), Quercetin (500mg BID), Resveratrol, Omega-3s, Boswellia</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">JAK/STAT Pathway</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>JAK/STAT (Cytokine Signaling):</strong></p>
                <p className="mt-2">Dysregulation: Persistent upregulation (esp. IL-6 driven) → chronic inflammation, T-cell exhaustion</p>
                <p className="mt-2">Acute activation correlates with Long COVID development</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Clinical Levers (JAK Inhibitors):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Baricitinib (2-4mg/day), Ruxolitinib</li>
                  <li>Specialist use, trials ongoing (REVERSE-LC for Baricitinib)</li>
                  <li>Monitor for immunosuppression, side effects</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hijacked Cell Signals (3): Fibrosis, Defense & Vascular Tone",
      subtitle: "Signaling: TGF-β, Nrf2 & RAAS Dysregulation",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">TGF-β & Nrf2 Pathways</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>TGF-β (Fibrosis & Repair):</strong></p>
                <p className="mt-2">Dysregulation: Overexpression in severe COVID survivors, linked to lung fibrosis and long-term functional impairment</p>
                <p className="mt-2">Clinical Levers: Anti-fibrotics (Pirfenidone, Nintedanib), Losartan (ARB), Tβ4 (investigational)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Nrf2 (Antioxidant Master Regulator):</strong></p>
                <p className="mt-2">Dysregulation: SARS-CoV-2 inhibits Nrf2, crippling cellular antioxidant defenses</p>
                <p className="mt-2">Clinical Levers: Sulforaphane, Curcumin, Resveratrol, EGCG, Quercetin, Nrf2-activating foods</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">RAAS System</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>RAAS (Blood Pressure & Inflammation):</strong></p>
                <p className="mt-2">Dysregulation: SARS-CoV-2 binding to ACE2 → ACE2 downregulation → RAAS imbalance (↑Ang II/AT1R axis) → vasoconstriction, inflammation, fibrosis</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Clinical Levers:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>ACE Inhibitors (ACEIs) / ARBs - if clinically indicated</li>
                  <li>Aldosterone Antagonists (Spironolactone)</li>
                  <li>Consider based on emerging research</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The '5 R' Framework Adapted for Long COVID",
      subtitle: "Clinical Application: A Functional & Longevity Medicine Approach",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Personalized Systems-Based Approach</h3>
            <p className="text-sm text-gray-800 mb-6">A personalized, systems-based approach is crucial. Think "Remove, Replace, Reinoculate, Repair, Rebalance" adapted.</p>
            
            <svg width="600" height="350" viewBox="0 0 600 350" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <circle cx="300" cy="175" r="60" fill="#ADD8E6" stroke="#4682B4" strokeWidth="2"/>
              <text x="300" y="170" textAnchor="middle" fontSize="14" fontWeight="bold">Personalized</text>
              <text x="300" y="185" textAnchor="middle" fontSize="14" fontWeight="bold">Long COVID Care</text>

              <g transform="translate(120, 70)">
                <circle cx="0" cy="0" r="45" fill="#FFFACD" stroke="#FFD700" strokeWidth="1.5"/>
                <text x="0" y="-5" textAnchor="middle" fontSize="11">Test & Assess</text>
                <text x="0" y="10" textAnchor="middle" fontSize="10">(Comprehensive)</text>
              </g>
              <line x1="245" y1="150" x2="160" y2="95" stroke="#777" strokeWidth="1.5"/>

              <g transform="translate(480, 70)">
                <circle cx="0" cy="0" r="45" fill="#FFFACD" stroke="#FFD700" strokeWidth="1.5"/>
                <text x="0" y="-5" textAnchor="middle" fontSize="11">Identify Root</text>
                <text x="0" y="10" textAnchor="middle" fontSize="10">Causes</text>
              </g>
              <line x1="355" y1="150" x2="440" y2="95" stroke="#777" strokeWidth="1.5"/>

              <g transform="translate(80, 280)">
                <circle cx="0" cy="0" r="45" fill="#FFFACD" stroke="#FFD700" strokeWidth="1.5"/>
                <text x="0" y="-5" textAnchor="middle" fontSize="11">Targeted</text>
                <text x="0" y="10" textAnchor="middle" fontSize="10">Therapies</text>
              </g>
              <line x1="245" y1="200" x2="120" y2="260" stroke="#777" strokeWidth="1.5"/>

              <g transform="translate(520, 280)">
                <circle cx="0" cy="0" r="45" fill="#FFFACD" stroke="#FFD700" strokeWidth="1.5"/>
                <text x="0" y="-5" textAnchor="middle" fontSize="11">Lifestyle</text>
                <text x="0" y="10" textAnchor="middle" fontSize="10">Optimization</text>
              </g>
              <line x1="355" y1="200" x2="480" y2="260" stroke="#777" strokeWidth="1.5"/>

              <g transform="translate(300, 295)">
                <circle cx="0" cy="0" r="45" fill="#FFFACD" stroke="#FFD700" strokeWidth="1.5"/>
                <text x="0" y="-5" textAnchor="middle" fontSize="11">Monitor &</text>
                <text x="0" y="10" textAnchor="middle" fontSize="10">Adapt</text>
              </g>
              <line x1="300" y1="235" x2="300" y2="250" stroke="#777" strokeWidth="1.5"/>
            </svg>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Principles</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Thorough Assessment:</strong> Comprehensive history and symptom tracking</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Comprehensive Testing:</strong> Lab testing to uncover underlying imbalances</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Foundational Health:</strong> Address Gut, Nutrition, Sleep, Stress, Movement (paced)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Prioritized Interventions:</strong> Based on individual findings and dominant mechanisms</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Iterative Process:</strong> Monitor progress, adjust therapies</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Uncovering Imbalances: A Comprehensive Lab Approach",
      subtitle: "Diagnostic Workup: Key Areas to Investigate",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Laboratory Assessment Strategy</h3>
            <p className="text-sm text-gray-800 mb-6">No single "Long COVID test." A panel approach is needed.</p>
            
            <svg width="700" height="450" viewBox="0 0 700 450" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>
                {`.header { font-weight: bold; fill: #333; font-size: 13px; }
                 .cell { fill: #555; font-size: 12px; }
                 .row-line { stroke: #ccc; stroke-width: 1; }`}
              </style>
              <rect x="10" y="10" width="680" height="30" fill="#E0E0E0"/>
              <text x="80" y="30" className="header">Category</text>
              <text x="300" y="30" className="header">Key Biomarkers</text>
              <text x="550" y="30" className="header">Rationale</text>

              <text x="20" y="60" className="cell" fontWeight="bold">Inflammation/Immune</text>
              <text x="200" y="60" className="cell">hsCRP, ESR, Cytokines (IL-6, TNFα), T/B/NK subsets, AutoAbs</text>
              <text x="500" y="60" className="cell">Assess chronic inflam, immune dysreg.</text>
              <line x1="10" y1="75" x2="690" y2="75" className="row-line"/>

              <text x="20" y="95" className="cell" fontWeight="bold">Coagulation/Endothelial</text>
              <text x="200" y="95" className="cell">D-dimer, Fibrinogen, PAI-1, vWF, Platelets, Microclot assays*</text>
              <text x="500" y="95" className="cell">Eval hypercoag, endothelial health</text>
              <line x1="10" y1="110" x2="690" y2="110" className="row-line"/>

              <text x="20" y="130" className="cell" fontWeight="bold">Metabolic/Mitochondrial</text>
              <text x="200" y="130" className="cell">FG/FI, HbA1c, Lipids, Organic Acids, Lactate, CoQ10, Thiamine</text>
              <text x="500" y="130" className="cell">Assess metabolic health, mito energy</text>
              <line x1="10" y1="145" x2="690" y2="145" className="row-line"/>

              <text x="20" y="165" className="cell" fontWeight="bold">Nutritional/Redox</text>
              <text x="200" y="165" className="cell">Vit D, B12, Folate, Mg, Zn, Se, GSH, 8-OHdG, MDA</text>
              <text x="500" y="165" className="cell">Identify deficiencies, oxidative stress</text>
              <line x1="10" y1="180" x2="690" y2="180" className="row-line"/>

              <text x="20" y="200" className="cell" fontWeight="bold">Hormonal</text>
              <text x="200" y="200" className="cell">Thyroid (TSH,fT3,fT4,rT3,Abs), Cortisol (saliva/urine), Sex hormones</text>
              <text x="500" y="200" className="cell">Assess HPA, thyroid, sex hormone balance</text>
              <line x1="10" y1="215" x2="690" y2="215" className="row-line"/>

              <text x="20" y="235" className="cell" fontWeight="bold">Gut Health</text>
              <text x="200" y="235" className="cell">Stool Analysis (Microbiome, inflam, permeability), Zonulin, LPS</text>
              <text x="500" y="235" className="cell">Eval dysbiosis, leaky gut, GI inflam</text>
              <line x1="10" y1="250" x2="690" y2="250" className="row-line"/>

              <text x="20" y="270" className="cell" fontWeight="bold">Viral Reactivation</text>
              <text x="200" y="270" className="cell">EBV, CMV, HHV-6 (IgG/IgM, PCR if indicated)</text>
              <text x="500" y="270" className="cell">Investigate persistent triggers</text>
              <line x1="10" y1="285" x2="690" y2="285" className="row-line"/>
              <text x="200" y="300" className="cell" fontSize="10">*Microclot assays often research/specialty.</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Building Resilience: Personalized Therapeutic Interventions",
      subtitle: "Therapeutic Strategies: A Multi-Modal Approach",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Foundational Lifestyle</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Diet:</strong> Anti-inflammatory, nutrient-dense, low-glycemic, personalized (e.g., low histamine, elimination if needed)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Activity:</strong> Strict Pacing for Post-Exertional Malaise (PEM). Gentle movement as tolerated. Avoid "pushing through."</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Stress Management:</strong> Mindfulness, meditation, HRV biofeedback, nature</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Sleep Hygiene:</strong> Optimize for restorative sleep</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Targeted Supplementation</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Based on Biomarkers & Mechanisms:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Mitochondrial support (CoQ10, Carnitine, ALA, B-vitamins)</li>
                  <li>Antioxidants & Nrf2 activators (GSH, NAC, Sulforaphane, Curcumin)</li>
                  <li>NAD⁺ precursors (NR, NMN)</li>
                  <li>Gut healing (Probiotics, Prebiotics, Butyrate, L-Glutamine)</li>
                  <li>Immune modulators (Vitamin D, Zinc, Selenium, Quercetin)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Advanced Therapies</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Repurposed Medications:</strong> Metformin, Low-Dose Naltrexone, JAK inhibitors (Baricitinib)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Investigational Therapies:</strong> Senolytics (D+Q, Fisetin), Autophagy enhancers (Spermidine, Rapamycin), HBOT</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Key Principle:</strong> Start low, go slow. Monitor response. Personalized protocols are essential.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Spotlight on Research: Nattokinase for Microclots & Spike Protein",
      subtitle: "Key Study Highlight: Nattokinase & Microclots",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Research Background</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Background:</strong> Persistent microclots and potential residual spike protein are implicated in Long COVID symptoms</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Key Researchers:</strong> Kell DB, Laubscher GJ, Pretorius E. et al. (various papers 2022-2024)</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Findings</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>In vitro studies:</strong> Demonstrate nattokinase can degrade SARS-CoV-2 spike protein</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Microclot Discovery:</strong> Fibrinaloid microclots are prevalent in Long COVID plasma and are resistant to normal fibrinolysis</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Therapeutic Potential:</strong> Nattokinase (and lumbrokinase) show potential in degrading these anomalous microclots in vitro</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Clinical Relevance:</strong> Nattokinase may offer a therapeutic avenue for symptoms linked to microclots and impaired microcirculation (fatigue, brain fog, dyspnea)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Typical Dosing:</strong> 2000 FU (100mg) BID to 4000 FU (200mg) BID, away from food</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Monitoring/Cautions:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Bleeding risk (especially with anticoagulants/antiplatelets)</li>
                  <li>Avoid if active bleeding, surgery planned, bleeding disorders</li>
                  <li>Start low, assess tolerance</li>
                  <li>Consider alongside other coagulation/endothelial support</li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Note:</strong> Further clinical trials are needed to confirm efficacy and safety in Long COVID populations</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Long COVID Through the SHIFT Framework Lens",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">PASC Pathophysiology Mapped to Core Aging Biology</h3>
            <p className="text-sm text-gray-700">How Long COVID disrupts fundamental aging pathways and targeted interventions</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-md font-medium text-amber-800">Sensing & Metabolic Dysfunction</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>PASC Pathology:</strong> Metabolic inflexibility, insulin resistance, mitochondrial dysfunction, impaired cellular energy sensing</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Disrupted Pathways:</strong> AMPK suppression, mTOR dysregulation, NAD+ depletion, glucose utilization impairment</p>
              <p className="text-xs text-gray-700"><strong>Targeted Interventions:</strong> NAD+ precursors, metformin, time-restricted eating, AMPK activators, mitochondrial cocktail</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-md font-medium text-green-800">Homeostatic Defense Impairment</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>PASC Pathology:</strong> Compromised stress response, oxidative damage, impaired cellular repair mechanisms, dysautonomia</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Disrupted Pathways:</strong> Nrf2 suppression, autophagy dysfunction, heat shock protein impairment, HPA axis disruption</p>
              <p className="text-xs text-gray-700"><strong>Targeted Interventions:</strong> NAC, glutathione support, sauna therapy, meditation, adaptogenic herbs, breathwork</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-md font-medium text-red-800">Chronic Inflammation & Immune Dysregulation</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>PASC Pathology:</strong> Persistent cytokine elevation, autoimmune reactions, T-cell exhaustion, complement activation</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Disrupted Pathways:</strong> NF-κB hyperactivation, inflammasome upregulation, Th1/Th2 imbalance, regulatory T-cell dysfunction</p>
              <p className="text-xs text-gray-700"><strong>Targeted Interventions:</strong> Omega-3 fatty acids, curcumin, low-dose naltrexone, IVIG, plasmapheresis, immunomodulatory protocols</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-md font-medium text-purple-800">Fibrosis & Vascular Remodeling</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>PASC Pathology:</strong> Endothelial dysfunction, microclot formation, tissue fibrosis, vascular inflammation</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Disrupted Pathways:</strong> TGF-β activation, collagen deposition, platelet dysfunction, complement-mediated damage</p>
              <p className="text-xs text-gray-700"><strong>Targeted Interventions:</strong> Anticoagulation protocols, nattokinase, HBOT, stellate ganglion blocks, vascular rehabilitation</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-md font-medium text-blue-800">Circadian & Neurological Disruption</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>PASC Pathology:</strong> Sleep architecture disruption, circadian dysregulation, cognitive dysfunction, autonomic imbalance</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Disrupted Pathways:</strong> Clock gene dysfunction, melatonin suppression, neuroinflammation, vagal nerve impairment</p>
              <p className="text-xs text-gray-700"><strong>Targeted Interventions:</strong> Melatonin optimization, light therapy, cognitive rehabilitation, vagal nerve stimulation, sleep hygiene</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-md font-medium text-gray-800 mb-2">Integrated Long COVID Treatment Strategy</h3>
            <p className="text-xs text-gray-700">Long COVID represents multi-system dysfunction across all SHIFT domains simultaneously. Successful treatment requires addressing the interconnected pathophysiology through multi-modal interventions targeting metabolic restoration, immune rebalancing, vascular repair, and neurological rehabilitation. Sequential therapy protocols based on individual SHIFT domain severity patterns guide personalized recovery approaches.</p>
          </div>
        </div>
      )
    },
    {
      title: "Long COVID Clinical Implementation",
      subtitle: "Applying SHIFT Framework to Complex Cases",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Assessment Areas</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Metabolic Function:</strong> AMPK activity, mitochondrial health</p>
                <p><strong>Immune Status:</strong> Inflammatory markers, T-cell function</p>
                <p><strong>Neurological:</strong> Cognitive assessment, autonomic function</p>
                <p><strong>Cardiovascular:</strong> Endothelial function, clotting factors</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Treatment Priorities</h3>
              <div className="space-y-2 text-sm text-green-800">
                <p><strong>Energy Restoration:</strong> Mitochondrial support, NAD+ optimization</p>
                <p><strong>Immune Rebalancing:</strong> Anti-inflammatory interventions</p>
                <p><strong>Neuroplasticity:</strong> Brain training, neuroprotection</p>
                <p><strong>Vascular Health:</strong> Endothelial support, circulation</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Path Forward: A Holistic & Optimizing Approach",
      subtitle: "Conclusion: Restoring Health & Optimizing Longevity Post-COVID",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 p-8 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Paradigm Shift in Long COVID Care</h3>
            <p className="text-lg text-gray-700 mb-6">From symptom suppression to cellular restoration</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Insights</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Complex Condition:</strong> Long COVID is a complex, chronic, multisystemic condition rooted in deep-seated biological dysfunctions</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Powerful Framework:</strong> A Longevity & Functional Medicine perspective offers a comprehensive approach that moves beyond symptom suppression to address root causes</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Cellular Focus:</strong> Focuses on restoring cellular health, immune homeostasis, metabolic efficiency, and redox balance</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Strategies</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Comprehensive Assessment:</strong> Personalize interventions through detailed evaluation</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Multi-Target Approach:</strong> Target multiple interconnected pathways simultaneously</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Patient Empowerment:</strong> Enable patients with lifestyle modifications and self-management tools</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Evidence-Based Therapeutics:</strong> Utilize evidence-informed nutraceuticals, repurposed drugs, and emerging therapies</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Ultimate Goal</h3>
            <p className="text-sm text-gray-700">
              Not just recovery from Long COVID, but enhancement of long-term health, resilience, and healthspan
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Questions & Discussion",
      subtitle: "Q&A",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-12 rounded-lg text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Questions & Discussion
            </h1>
            
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl mx-auto">
              <div className="space-y-6">
                <div className="text-2xl text-blue-600 mb-4">💬</div>
                <p className="text-lg text-gray-700">
                  Thank you for your attention to this comprehensive exploration of Long COVID through the lens of longevity and functional medicine.
                </p>
                <p className="text-base text-gray-600">
                  I welcome your questions, comments, and clinical insights on applying these frameworks in practice.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Discussion Topics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Clinical Implementation:</strong> Practical strategies for integrating these approaches</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Patient Cases:</strong> Specific scenarios and treatment considerations</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Research Updates:</strong> Latest findings and emerging therapies</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Collaborative Care:</strong> Multidisciplinary team approaches</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Selected References",
      subtitle: "Evidence Base & Further Reading",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">General & Definitions</h3>
            <div className="text-xs text-gray-800 space-y-2">
              <p>Davis HE, et al. Long COVID: major findings, mechanisms and recommendations. Nat Rev Microbiol. 2023;21(3):133-146.</p>
              <p>National Academies of Sciences, Engineering, and Medicine. 2024. Examining the Working Definition for Long COVID. Washington, DC: The National Academies Press.</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Immune Dysregulation & Inflammation</h3>
            <div className="text-xs text-gray-800 space-y-2">
              <p>Klein J, et al. Distinguishing features of Long COVID identified through immune profiling. Nature. 2023;623(7985):139-148.</p>
              <p>Castanares-Zapatero D, et al. Pathophysiology and mechanism of action of COVID-19. Ann N Y Acad Sci. 2020;1475(1):146-162.</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Microclots & Endothelial Dysfunction</h3>
            <div className="text-xs text-gray-800 space-y-2">
              <p>Pretorius E, et al. Persistent clotting protein pathology in Long COVID/Post-Acute Sequelae of COVID-19 (PASC) is accompanied by increased levels of antiplasmin. Cardiovasc Diabetol. 2021;20(1):172.</p>
              <p>Oba M, et al. Nattokinase Degrades the Spike Protein of SARS-CoV-2. Molecules. 2022;27(17):5391.</p>
              <p>Varga Z, et al. Endothelial cell infection and endotheliitis in COVID-19. Lancet. 2020;395(10234):1417-1418.</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Mitochondrial & Cellular Mechanisms</h3>
            <div className="text-xs text-gray-800 space-y-2">
              <p>Phetsouphanh C, et al. Immunological dysfunction persists for 8 months following initial mild to moderate SARS-CoV-2 infection. Nat Immunol. 2022;23(2):210-216.</p>
              <p>Gassen NC, et al. SARS-CoV-2-mediated suppression of autophagy signalling. Nat Cell Biol. 2021;23(8):850-861.</p>
              <p>Lonsdale D. Thiamine deficiency and COVID-19. BMJ Nutrition, Prevention & Health. 2020;3(1):148-149.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-blue-50">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Long COVID" fileName="LongCovid" />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                SHIFT Framework
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Long COVID
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A Longevity, Functional Medicine and Health Optimization Perspective
            </p>
          </div>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6">
          <span className="text-sm text-gray-500">
            Slide {currentSlide + 1} of {slides.length}
          </span>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className="bg-white shadow-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Link href="/" className="hover:text-blue-600 transition-colors">
                <Home className="h-4 w-4" />
              </Link>
              <span>/</span>
              <Link href="/mentorship" className="hover:text-blue-600 transition-colors">
                Mentorship
              </Link>
              <span>/</span>
              <Link href="/mentorship" className="hover:text-blue-600 transition-colors flex items-center">
                <BookOpen className="h-4 w-4 mr-1" />
                Lectures
              </Link>
              <span>/</span>
              <span className="text-blue-600 font-medium">Long COVID</span>
            </div>

            {/* Slide Counter */}
            <div className="text-sm text-gray-600">
              Slide {currentSlide + 1} of {slides.length}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Slide Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 min-h-[600px]">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {slides[currentSlide].title}
            </h1>
            {slides[currentSlide].subtitle && (
              <h2 className="text-xl text-gray-600">
                {slides[currentSlide].subtitle}
              </h2>
            )}
          </div>
          
          <div className="prose prose-lg max-w-none">
            {slides[currentSlide].content}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Previous
          </button>

          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="h-5 w-5 ml-2" />
          </button>
        </div>

        {/* Audio Lecture Section */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Audio Lecture</h3>
          <div className="bg-blue-50 p-6 rounded-lg">
            <iframe
              src="https://share.descript.com/embed/DnBbOYoXklt"
              width="100%"
              height="360"
              frameBorder="0"
              allowFullScreen
              className="rounded-lg shadow-md"
              title="Long COVID Audio Lecture"
            ></iframe>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Listen to the comprehensive audio lecture covering Long COVID pathophysiology, mechanisms, and clinical strategies through the lens of longevity and functional medicine.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-2">
            Progress: {Math.round(((currentSlide + 1) / slides.length) * 100)}%
          </p>
        </div>
      </div>
    </div>
  );
}